/* eslint-disable @typescript-eslint/no-explicit-any */
import { ELC_ACCOUNT, ELC_CHECKOUT } from 'elc-service-view-names';
import { Service, ServiceInitializer } from 'elc-service';
import { emit, query, serviceQueries, events } from 'elc-service-bus';
import { root } from 'elc-universal-utils';
import { mapConfigToProps } from './DrupalConfigStore';
import { defaultAppRoutes } from '../routes/index';
import { ServiceLazyLoader } from './ServiceLazyLoader';
import { getServiceBusController, diContainer, serviceNames } from './diContainer';
import { ServiceBusLogHook, ELCLogger } from 'elc-logging';
import { startMark, endMark } from './perfMark';

import { name, version } from '../../package.json';
const SIGN_IN_SERVICE = 'elc-service-sign-in';
const TRANSLATIONS_SERVICE = 'elc-service-translations';
const CONFIG_MANAGER_SERVICE = 'elc-service-config-manager';
const SAVE_FOR_LATER_SERVICE = 'elc-service-save-for-later';
const PRODCAT_SERVICE = 'elc-service-prodcat';
const MANDATORY_SERVICES = [
    SIGN_IN_SERVICE,
    TRANSLATIONS_SERVICE,
    CONFIG_MANAGER_SERVICE,
    PRODCAT_SERVICE
];

interface IServiceRegistryVersions {
    [key: string]: string;
}

export class EcommApp extends Service {
    private disabledServices: string[] = [];
    protected logger: ELCLogger = new ELCLogger('EcommApp');
    protected serviceLazyLoader: ServiceLazyLoader = new ServiceLazyLoader();

    constructor(serviceInitializer: ServiceInitializer = getServiceBusController) {
        super(name, version, serviceInitializer);
        this.initializeServiceVersions();
    }
    public async start(appConfig: any = {}) {
        if (this.isStarted) {
            return;
        }
        root.GlobalServiceBus.applyHook(ServiceBusLogHook);
        this.serviceLazyLoader.load(appConfig);
        appConfig.routes = Object.assign(appConfig.routes || {}, defaultAppRoutes);
        const props = mapConfigToProps(appConfig);
        // Use @ as a service and version separator if there is at least one script
        // in page that uses that convention
        const useAtSignSeparator = Array.from(document.querySelectorAll('script'))
            .map(s => s.src)
            .filter(src => src.indexOf(props.cdnPath) !== -1)
            .some(src => src.split('@').length > 1);
        const versionSeparator = useAtSignSeparator ? '@' : '/';

        const replaceFunction = (originalSrc: string) => {
            const sourceInfo = originalSrc.slice(originalSrc.indexOf('elc-'));
            const [serviceName, chunkName] = sourceInfo.split('/');
            const serviceVersion = props.services[serviceName];

            return `${props.cdnPath}/${serviceName}${versionSeparator}${serviceVersion}/${chunkName}`;
        };
        root.replaceSrc = root.replaceSrc || replaceFunction;
        void super.start(props);
        this.setupHttpTransport(props.defaultDomain, props.businessUnitId, props.clientId);
        this.registerViews();
        diContainer.get(serviceNames.serviceBusController);
        await this.bootstrap(props);
    }
    private setupHttpTransport = (
        defaultDomain: string,
        businessUnitId: string,
        clientId: string
    ) => {
        root.HttpTransport.setBaseUrl(defaultDomain);
        root.HttpTransport.addGlobalHeaders({
            'Business-Unit': businessUnitId,
            ClientId: clientId || 'stardust-fe-client'
        });
    };

    private startMandatoryService = async (serviceName: string, props: any) => {
        try {
            const perfMarkTag = `start_${serviceName}`;

            startMark(perfMarkTag);
            await root.ServiceRegistry[serviceName].start(props);
            endMark(perfMarkTag);
        } catch (err) {
            this.logger.error(err);
        }
    };

    private initializeComponentRenderer = async () => {
        if (root.ComponentRenderer && document.body) {
            const initialMarkup = typeof document !== 'undefined' ? document.body.innerHTML : '';

            startMark('initialize_component_renderer');
            await root.ComponentRenderer.init(initialMarkup);
            endMark('initialize_component_renderer');
        } else {
            this.logger.error({
                triggerType: 'EcommApp.initializeComponentRenderer',
                message: 'No ComponentRenderer on the global object or no body in document'
            });
        }
    };

    private bootstrap = async (props: any) => {
        try {
            // start mandatory services one by one in order
            await MANDATORY_SERVICES.reduce(async (previousPromise, nextService: string) => {
                await previousPromise;

                return this.startMandatoryService(nextService, props);
            }, Promise.resolve());

            startMark('fetch_feature_flags_config');
            await this.setupDisabledServices();
            endMark('fetch_feature_flags_config');

            startMark('start_services');
            await this.startServices(props);
            endMark('start_services');

            this.initializeComponentRenderer();
        } catch (err) {
            this.logger.error(err);
        }
    };
    private startServices = async (props: any) => {
        const servicesToInitialize = Object.keys(root.ServiceRegistry).filter(
            serviceName =>
                serviceName !== this.name &&
                serviceName !== SIGN_IN_SERVICE &&
                serviceName !== TRANSLATIONS_SERVICE &&
                serviceName !== CONFIG_MANAGER_SERVICE &&
                serviceName !== PRODCAT_SERVICE &&
                this.disabledServices.indexOf(serviceName) < 0 &&
                //@ts-ignore
                !root.ServiceRegistry[serviceName].isLazy &&
                !root.ServiceRegistry[serviceName].isStarted
        );
        for (const serviceName of servicesToInitialize) {
            const service = root.ServiceRegistry && root.ServiceRegistry[serviceName];
            if (service && !service.isStarted) {
                startMark(`start_service_${serviceName}`);
                await service.start(props);
                emit(events.SERVICE_STARTED, { serviceName: serviceName });
                endMark(`start_service_${serviceName}`);
            }
        }
    };

    public setupDisabledServices = async () => {
        const config = await query(serviceQueries.GET_CONFIG, ['isSaveForLaterFeatureEnabled']);
        if (!config.isSaveForLaterFeatureEnabled) {
            this.disabledServices.push(SAVE_FOR_LATER_SERVICE);
        }
    };

    private registerViews() {
        this.registerView(ELC_ACCOUNT, () =>
            import(/* webpackChunkName: 'Account' */ '../exported/account/Account')
        );
        this.registerView(ELC_CHECKOUT, () =>
            import(/* webpackChunkName: 'Checkout' */ '../exported/checkout/Checkout')
        );
    }

    private initializeServiceVersions() {
        root.getServiceVersions = () => {
            const serviceRegistryVersions: IServiceRegistryVersions = {};
            const nonServiceVersions = root.versions || {};
            Object.keys(root.ServiceRegistry).forEach(key => {
                if (root.ServiceRegistry[key].version) {
                    serviceRegistryVersions[key] = root.ServiceRegistry[key].version;
                }
            });
            console.table({ ...serviceRegistryVersions, ...nonServiceVersions });
        };
    }
}
